import { watcherServiceClient } from '@lib/watcherServiceClient';
import type { AuditEvent, AuditFilter } from '@/types/audit';

/**
 * Watcher-service paginated list response envelope.
 * Defined here (not in @types/audit) because it is a transport detail
 * specific to this service.
 */
export interface AuditListResponse {
  data: AuditEvent[];
  meta: {
    page: number;
    limit: number;
    total: number;
  };
}

/**
 * GET /v1/audits — paginated audit-event list.
 *
 * Maps the AuditFilter object 1:1 to query string params (camelCase per
 * backend/watcher-service/internal/handler/audit_handler.go):
 *   - resourceType, resourceId, actorType, actorId, eventName,
 *     fromDate, toDate — direct param names.
 *   - filterMap entries become `fm_<key>=<value>` (multiple AND together).
 *     The inner key follows the backend's camelCase convention
 *     (e.g. `fm_organizationId`, `fm_tenantId`).
 *
 * Permission: AUDIT_READ. The hook layer (useAuditEvents) is responsible
 * for permission gating via the query's `enabled` arg — this function
 * itself does no client-side authorization.
 */
export async function getAuditEvents(
  filter: AuditFilter,
  page: number,
  limit: number,
): Promise<AuditListResponse> {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  if (filter.resourceType) params.set('resourceType', filter.resourceType);
  if (filter.resourceId) params.set('resourceId', filter.resourceId);
  if (filter.actorType) params.set('actorType', filter.actorType);
  if (filter.actorId) params.set('actorId', filter.actorId);
  if (filter.eventName) params.set('eventName', filter.eventName);
  if (filter.fromDate) params.set('fromDate', filter.fromDate);
  if (filter.toDate) params.set('toDate', filter.toDate);
  if (filter.filterMap) {
    for (const [k, v] of Object.entries(filter.filterMap)) {
      params.set(`fm_${k}`, v);
    }
  }

  const response = await watcherServiceClient.get<AuditListResponse>(
    `/v1/audits?${params.toString()}`,
  );
  return response.data;
}
